/**
 * ChartEmptyState — Estado vacío reutilizable de las gráficas.
 *
 * QUÉ HACE: ocupa el lugar de un gráfico cuando no hay datos para el periodo o
 *   la vista (arreglo vacío o series en cero). Dibuja una mini-gráfica fantasma
 *   (barras que suben en orden de lectura con la animación compartida), un
 *   título y una descripción opcional, con la misma paleta del resto de las
 *   tarjetas. Así ninguna tarjeta queda con ejes vacíos, líneas planas o en
 *   blanco. Es el estado que usan TODOS los primitivos y tarjetas de gráfico.
 *
 *   **Adaptativo**: mide su contenedor (`useChartSize`, el mismo que los
 *   gráficos) y reduce barras, tipografía y descripción según el ancho/alto,
 *   igual que el resto de las vistas. En tarjetas muy pequeñas (cuadrícula
 *   movible a 1 columna y ~96 px) conserva solo la silueta; con espacio muestra
 *   título y descripción.
 *
 * QUÉ INVOCA: React, `useChartSize` y las utilidades CSS de animación
 *   compartidas (`animate-chart-rise` + `.chart-rise-bar` y `barStaggerDelay`).
 *   No usa Recharts ni conoce datos.
 * QUÉ EXPORTA: ChartEmptyState.
 * QUÉ NO DEBE HACER: no calcula datos, no lee storage ni decide si hay datos;
 *   solo pinta el estado vacío cuando el consumidor lo indica.
 */
import type { CSSProperties, ReactNode } from "react";
import { barStaggerDelay } from "@/lib/animation/motion";
import { useChartSize } from "./useChartSize";

/** Alturas relativas de las barras fantasma (una silueta de gráfico). */
const GHOST_BARS = [38, 62, 46, 84, 58, 72] as const;

interface ChartEmptyStateProps {
  /** Título principal (por defecto "Sin datos para este periodo"). */
  title?: string;
  /** Texto de apoyo opcional bajo el título. */
  description?: string;
  /** Nodo opcional sobre el título (p. ej. un icono). */
  icon?: ReactNode;
  /** Ocupa todo el alto disponible de la tarjeta (gráficos que se estiran). */
  fillHeight?: boolean;
  /** Alto mínimo del contenedor cuando no llena la tarjeta (px). */
  minHeight?: number;
  /** Clases adicionales del contenedor. */
  className?: string;
}

export const ChartEmptyState = ({
  title = "Sin datos para este periodo",
  description,
  icon,
  fillHeight = false,
  minHeight = 160,
  className = "",
}: ChartEmptyStateProps) => {
  /**
   * Cuando llena la tarjeta se mide el **padre** (el alto real de la tarjeta),
   * porque el propio contenedor puede quedar con alto `auto` al no tener una
   * altura definida. Sin `fillHeight` se mide a sí mismo.
   */
  const { ref, width, height } = useChartSize<HTMLDivElement>({
    observeParent: fillHeight,
  });

  /** Tamaño real medido; si aún no se midió, se asume el caso completo. */
  const measured = width > 0 || height > 0;
  const w = measured ? width : 320;
  const h = measured ? height : 200;

  /** Muy pequeña: solo la silueta (cuadrícula a 1 columna, ~96 px). */
  const tiny = measured && (h < 104 || w < 120);
  /** Compacta: barras y título reducidos, sin descripción. */
  const compact = measured && (h < 168 || w < 190);

  const bars = tiny ? GHOST_BARS.slice(1, 5) : GHOST_BARS;
  const barsHeight = tiny ? "h-4" : compact ? "h-7" : "h-12";
  const barWidth = tiny ? "w-1" : compact ? "w-1.5" : "w-2";
  const barsGap = tiny ? "gap-1" : "gap-1.5";

  const showBars = !measured || h >= 52;
  const showTitle = !measured || h >= 60;
  const showDescription =
    Boolean(description) && (!measured || (h >= 188 && w >= 180));

  const titleClass = tiny
    ? "text-[10px] leading-tight"
    : compact
      ? "text-xs leading-tight"
      : "text-sm";

  const gapClass = tiny ? "gap-1 px-2" : compact ? "gap-2 px-3" : "gap-3 px-6";

  return (
    <div
      ref={ref}
      role="status"
      className={`flex w-full flex-col items-center justify-center overflow-hidden text-center ${gapClass} ${
        fillHeight ? "h-full min-h-0 flex-1" : ""
      } ${className}`}
      style={fillHeight ? undefined : { minHeight }}
    >
      {showBars && (
        <div
          className={`flex ${barsHeight} items-end justify-center ${barsGap} opacity-70`}
          aria-hidden="true"
        >
          {bars.map((barHeight, index) => (
            <span
              key={index}
              className={`chart-rise-bar animate-chart-rise ${barWidth} rounded-t-sm bg-gradient-to-t from-gray-200 to-gray-300 dark:from-slate-700 dark:to-slate-600`}
              style={
                {
                  height: `${barHeight}%`,
                  animationDelay: barStaggerDelay(index),
                } as CSSProperties
              }
            />
          ))}
        </div>
      )}
      {icon}
      {showTitle && (
        <p
          className={`max-w-full font-semibold text-gray-500 dark:text-gray-300 ${titleClass}`}
        >
          {title}
        </p>
      )}
      {showDescription && (
        <p className="max-w-[18rem] text-xs leading-relaxed text-gray-400 dark:text-gray-500">
          {description}
        </p>
      )}
    </div>
  );
};
